import useCallbackRef from 'hooks/useCallbackRef';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import React, {
  ForwardRefExoticComponent,
  RefAttributes,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  isQABuyDrafter,
  isQuestionAnswerApproved,
} from 'router/subrouters/DataRoom/utils';
import {
  Question,
  QuestionManagingDrawerCommonProps,
} from 'router/subrouters/DataRoom/models/QAndA';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import QAndAContext from 'router/subrouters/DataRoom/providers/QAndA/QAndA.context';
import PreviewQuestionDrawer from '../../PreviewQuestionDrawer';
import { QUESTION_QUERY_ARRAYS } from '../../QAndARoleFilter/data';
import { SideDrawerRef } from '../../SideDrawer/SideDrawer.component';
import { EditQuestionDrawer, ManageQuestion } from '../components';
import { ManageActionKeys, generateManageKey } from '../utils';
import { PreviewSideDrawerRef } from '../../PreviewQuestionDrawer/PreviewQuestionDrawer.component';

const MANAGE_CONTENT: Record<
  ManageActionKeys,
  ForwardRefExoticComponent<
    QuestionManagingDrawerCommonProps & RefAttributes<SideDrawerRef>
  >
> = {
  edit: EditQuestionDrawer,
  manage: ManageQuestion,
  preview: PreviewQuestionDrawer,
  else: null,
};

type DrawerRefProps = SideDrawerRef & PreviewSideDrawerRef;

const useManageQuestion = (question: Question) => {
  const { t } = useTranslation();
  const { qaRole } = useContext(DataRoomContext);
  const [drawer, ref] = useCallbackRef<DrawerRefProps>(null);
  const { refetch, updateQuestion } = useContext(QAndAContext);
  const {
    currentUser: { _id },
  } = useContext(CurrentUserContext);

  const { status, userId, dataRoomParentQuestionId } = question;

  const key = useMemo(
    () => generateManageKey(status, qaRole),
    [qaRole, status],
  );

  const YourDrawerType: ForwardRefExoticComponent<
    QuestionManagingDrawerCommonProps & RefAttributes<SideDrawerRef>
  > = useMemo(() => MANAGE_CONTENT[key], [key]);

  const manageButtonText = useMemo(() => {
    if (QUESTION_QUERY_ARRAYS.IN_REVIEW_BUY_SIDE.includes(status))
      return 'review';
    if (key === 'preview') return 'preview';
    return 'manage';
  }, [key, status]);

  const cantManageQuestion =
    !YourDrawerType || (_id !== userId && isQABuyDrafter(qaRole));

  const canFollowUp =
    !dataRoomParentQuestionId && isQuestionAnswerApproved(status);

  const YourDrawer = useMemo(
    () =>
      !cantManageQuestion ? (
        <YourDrawerType
          ref={ref}
          canFollowUp={canFollowUp}
          question={question}
          onQuestionsUpdate={refetch}
          updateQuestion={updateQuestion}
        />
      ) : null,
    [
      YourDrawerType,
      canFollowUp,
      cantManageQuestion,
      question,
      ref,
      refetch,
      updateQuestion,
    ],
  );

  const onManageClick = useCallback(() => drawer.setOpen(true), [drawer]);

  const onFollowUpClick = useCallback(() => {
    drawer.setShowFollowUp(true);
    drawer.setOpen(true);
  }, [drawer]);

  return {
    YourDrawer,
    cantManageQuestion,
    manageButtonText: t(manageButtonText),
    followUpButtonText: t('askAFollowUp'),
    canFollowUp,
    onManageClick,
    onFollowUpClick,
  };
};

export default useManageQuestion;
